import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { CSSTransition } from 'react-transition-group'
import FullPageModal from './FullPageModal'
import SvgIcon from '../SvgIcon'
import { cn } from 'src/utils/cn'

function Modal({
  children,
  onClose,
  open,
  className,
  bodyClassName,
  hasDistanceFromTop,
  title,
  overrideTitleClassName = '',
  hiddeCloseButton = false,
  closeButtonClassNames = '',
  backdropClassNames = '',
  parent = undefined,
}) {
  const rootRef = useRef()
  // todo: replace with useClickOutside hook
  const handleOutsideClick = useCallback(
    (e) => {
      if (e.target === rootRef.current) {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    window.addEventListener('click', handleOutsideClick)

    return () => {
      window.removeEventListener('click', handleOutsideClick)
    }
  }, [handleOutsideClick, open])

  if (global.window === undefined) return <></>

  return createPortal(
    <CSSTransition
      in={open}
      timeout={500}
      classNames={{
        enter: 'opacity-0',
        enterActive: 'opacity-100 duration-500',
        exitActive: 'opacity-0 duration-500',
      }}
      unmountOnExit
      mountOnEnter
    >
      <div
        data-testid="modal"
        ref={rootRef}
        className={cn(
          'fixed z-50 flex items-center justify-center bg-gray-410 p-5',
          hasDistanceFromTop
            ? 'inset-x-0 top-16 bottom-0 lg:inset-0'
            : 'inset-0',
          backdropClassNames
        )}
        id="modal"
      >
        <div className={`relative rounded-lg bg-white ${className}`}>
          {!hiddeCloseButton && (
            <button
              type="button"
              className={cn(
                'absolute top-0 right-0 z-50 cursor-pointer px-5 text-2xl',
                closeButtonClassNames
              )}
              data-testid="close button"
              onClick={onClose}
              id="close-button"
            >
              <SvgIcon name="closeModal" />
            </button>
          )}
          <div className={cn('max-h-[80vh] overflow-y-auto', bodyClassName)}>
            {title ? (
              <h2
                className={
                  overrideTitleClassName
                    ? overrideTitleClassName
                    : 'text-md font-semibold leading-6 text-blue-700'
                }
              >
                {title}
              </h2>
            ) : null}
            {children}
          </div>
        </div>
      </div>
    </CSSTransition>,
    parent || global?.document?.body
  )
}

Modal.defaultProps = {
  open: false,
  hasDistanceFromTop: false,
}
Modal.propTypes = {
  children: PropTypes.element.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  overrideTitleClassName: PropTypes.string,
  open: PropTypes.bool,
  bodyClassName: PropTypes.string,
  hasDistanceFromTop: PropTypes.bool,
  hiddeCloseButton: PropTypes.bool,
  closeButtonClassNames: PropTypes.string,
  backdropClassNames: PropTypes.string,
}

export default Modal
export { FullPageModal, Modal }
